// utils/socket.ts
import serviceConfig from "config/serviceConfig";

type JsonListener = (msg: any) => void;

let ws: WebSocket | null = null;
let reconnectTimer: number | null = null;
let closedByUser = false;
let currentToken = "";

// ✅ multiple listeners
const listeners = new Set<JsonListener>();

const buildWsUrl = (token: string) => {
	const base = serviceConfig.api.websocketUrl.replace(/^http/, "ws");
	const path = `/ws/connect/`;
	return `${base}${path}?token=${encodeURIComponent(token)}`;
};

const clearReconnect = () => {
	if (reconnectTimer) window.clearTimeout(reconnectTimer);
	reconnectTimer = null;
};

const notifyAll = (msg: any) => {
	listeners.forEach((fn) => {
		try {
			fn(msg);
		} catch (e) {
			console.warn("[WS] listener error:", e);
		}
	});
};

const connectInternal = () => {
	if (!currentToken) return;

	const url = buildWsUrl(currentToken);

	// close previous instance if any (defensive)
	try {
		ws?.close();
	} catch (err) {
		// ignore close errors
	}

	ws = new WebSocket(url);

	ws.onopen = () => {
		console.log("[WS] connected:", url);
	};

	ws.onmessage = (evt) => {
		try {
			const data = JSON.parse(evt.data);
			notifyAll(data);
		} catch {
			// some servers send plain text pings etc
			console.warn("[WS] non-json message:", evt.data);
		}
	};

	ws.onerror = (e) => {
		console.warn("[WS] error", e);
	};

	ws.onclose = () => {
		ws = null;

		if (closedByUser) return;
		if (!currentToken) return; // no token -> don't reconnect

		clearReconnect();
		reconnectTimer = window.setTimeout(connectInternal, 2000);
		console.warn("[WS] closed. reconnecting...");
	};
};

/**
 * ✅ Call this ONCE in App.tsx after login (token exists).
 * It will connect (or reuse existing) and keep reconnecting.
 */
export const ensureWsConnected = (token: string) => {
	if (!token) return;

	// token changed -> force reconnect
	if (currentToken && currentToken !== token) {
		disconnectWs();
	}

	currentToken = token;
	closedByUser = false;

	// already open -> no-op
	if (ws && ws.readyState === WebSocket.OPEN) return;

	// connecting -> no-op
	if (ws && ws.readyState === WebSocket.CONNECTING) return;

	clearReconnect();
	connectInternal();
};

/**
 * ✅ Subscribe in any page/component.
 * App.tsx keeps the socket alive; pages only listen.
 */
export const subscribeWs = (listener: JsonListener) => {
	listeners.add(listener);
	return () => {
		listeners.delete(listener);
	};
};

export const disconnectWs = () => {
	closedByUser = true;
	currentToken = "";
	clearReconnect();

	try {
		ws?.close();
	} catch (err) {
		// ignore close errors
	}

	ws = null;
	listeners.clear(); // optional: remove all listeners on logout
};

export const getWs = () => ws;
